import {
	ChangeDetectionStrategy,
	Component,
	OnInit,
	Signal,
	WritableSignal,
	computed,
	input,
	signal
} from '@angular/core';
import { ScheduleApi } from 'api/schedule/schedule-api.js';
import { UserApi } from 'api/user/user-api.js';
import { DietDb } from 'lib/cosmos/diet/dietDb.js';
import { ScheduleDb } from 'lib/cosmos/schedule/scheduleDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { AvailableScheduleItemGroup, SaveSchedule, ScheduleItemRouteMap } from 'lib/ui/schedule/index.js';
import { map } from 'rxjs';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';

@Component({
	selector: 'mod-client-diet',
	templateUrl: './client-diet.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class ClientDietComponent extends TypeCoreComponent<UserDb.IUserItem> implements OnInit {
	public readonly isPdfView = input<boolean>(false);

	public readonly scheduleItemGroups: WritableSignal<AvailableScheduleItemGroup<DietDb.IDietItem>[]> = signal([]);
	public readonly scheduleContainers: WritableSignal<ScheduleDb.IScheduleContainer<DietDb.IDietItem>[]> = signal([]);
	public readonly customSchedules: WritableSignal<ScheduleDb.ISchedule<DietDb.IDietItem>[]> = signal([]);
	public readonly defaultSchedules: WritableSignal<ScheduleDb.ISchedule<DietDb.IDietItem>[]> = signal([]);
	public readonly scheduleTitle: WritableSignal<string | undefined> = signal(undefined);

	public readonly routeMap: ScheduleItemRouteMap<DietDb.IDietItem> = (itemId, selectedItem, container) => {
		const sharedData = this.sharedData();
		const authUser = this.authUser();

		if (this.isPdfView()) {
			return {
				main: ''
			};
		}

		if (authUser?.userType === 'trainer') {
			return {
				main: this.actionRoute(
					(routes) => routes.trainerClientRoutes['client-diet'].children.design,
					sharedData?.data?.id,
					container.id,
					itemId
				).getUrl()
			};
		}

		return {
			main: this.actionRoute(
				(routes) => routes.clientRoutes.diet.children.view,
				sharedData?.data?.id,
				container.id,
				itemId
			).getUrl()
		};
	};

	public readonly enableEdit: Signal<boolean> = computed(() => {
		if (this.isPdfView()) {
			return false;
		}
		if (this.authUser()?.userType === 'trainer') {
			return true;
		}

		return false;
	});

	constructor() {
		super();

		this.sharedDataInit = () => {
			this.initScheduleItems();
			this.initAvailableSchedules();
			this.initScheduleContainers();
		};
	}

	handleDeleteSchedule(schedule: ScheduleDb.ISchedule<any>): void {
		const scheduleId = schedule.id;
		if (!scheduleId) {
			throw new Error(`Missing id for schedule`);
		}
		super.subscribeToObservable(
			this.dependencies.httpService.deleteSingle('schedule', scheduleId).pipe(
				map((response) => {
					// add newly saved schedule to current set
					this.customSchedules.update((current) => {
						return current.filter((m) => m.id !== schedule.id);
					});

					super.notifyDeleted('Schedule');
				})
			)
		);
	}

	handleSaveScheduleAsTemplate(schedule: ScheduleDb.IBaseSchedule<any>): void {
		super.subscribeToObservable(
			this.dependencies.httpService
				.create<ScheduleDb.ISchedule<DietDb.IDietItem>, ScheduleDb.IBaseSchedule>({
					type: 'schedule',
					createModel: schedule
				})
				.pipe(
					map((response) => {
						// add newly saved schedule to current set
						this.customSchedules.update((current) => {
							return [...current, response.data];
						});

						super.notifyCreated('Schedule');
					})
				)
		);
	}

	handleSaveSchedule(schedule: SaveSchedule<DietDb.IDietItem>): void {
		const sharedData = this.sharedData();

		if (!sharedData) {
			return;
		}
		super.subscribeToObservable(
			this.dependencies.httpService
				.post<UserDb.IUserItem, UserApi.IEditDietPlanRequest>((builder) =>
					builder
						.withCustomApiRoute('user', 'editUserDiet')
						.withId(sharedData.data.id)
						.withData({
							title: schedule.title,
							dietContainers: schedule.containers.map((m) => {
								const nutritionContainer: UserDb.IUserDietContainer = {
									id: m.id,
									title: m.title,
									items: (m.items ?? []).map((m) => {
										const item: UserDb.IUserDietItem = {
											id: m.id,
											diet: m.data
										};
										return item;
									})
								};

								return nutritionContainer;
							})
						})
						.build()
				)
				.pipe(
					map((response) => {
						// update user
						super.updateSharedData({
							data: response.data,
							linkedItems: response.linkedItems
						});
						super.notifySaved('Schedule');
					})
				)
		);
	}

	private initScheduleItems(): void {
		if (!this.enableEdit()) {
			return;
		}

		super.subscribeToObservable(
			this.dependencies.httpService
				.get<DietDb.IDietItem[]>((builder) => builder.withCustomApiRoute('diet', 'custom').build())
				.pipe(
					map((response) => {
						this.scheduleItemGroups.set([
							{
								items: response.data.map((diet) => {
									const item: ScheduleDb.IScheduleItem<DietDb.IDietItem> = {
										title: diet.system.name,
										data: diet,
										id: diet.id,
										avatar: diet.avatar?.[0]?.image
									};

									return item;
								}),
								title: 'Custom diets'
							}
						]);
					})
				)
		);
	}

	private initAvailableSchedules(): void {
		if (!this.enableEdit()) {
			return;
		}
		super.subscribeToObservable(
			this.httpService
				.get<ScheduleApi.IAvailableSchedulesResponse>((builder) =>
					builder.withCustomApiRoute('schedule', 'getAvailableSchedules').build()
				)
				.pipe(
					map((response) => {
						this.customSchedules.set(response.data.customSchedules);
						this.defaultSchedules.set(response.data.defaultSchedules);
					})
				)
		);
	}

	private initScheduleContainers(): void {
		const sharedData = this.sharedData();
		const containers = sharedData?.data.nutrition?.containers;

		if (!sharedData || !containers?.length) {
			return;
		}

		if (!sharedData || !containers.length) {
			return;
		}

		this.scheduleTitle.set(sharedData?.data?.nutrition?.title);

		this.scheduleContainers.set(
			containers.map((container) => {
				const scheduleContainer: ScheduleDb.IScheduleContainer<DietDb.IDietItem> = {
					id: container.id,
					title: container.title,
					items: container.items.map((item) => {
						const scheduleItem: ScheduleDb.IScheduleItem<DietDb.IDietItem> = {
							data: item.diet,
							id: item.id,
							title: item.diet.system.name,
							avatar: item.diet.avatar?.[0]?.image
						};

						return scheduleItem;
					})
				};

				return scheduleContainer;
			})
		);
	}
}
